import React, { lazy, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Lazy load components for code splitting
const Login = lazy(() => import('./components/Login'));
const Signup = lazy(() => import('./components/Signup'));
const SignupSuccess = lazy(() => import('./components/SignupSuccess'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Dashboard = lazy(() => import('./components/dashboard'));
const ApproveUser = lazy(() => import('./components/ApproveUser'));
const Chat = lazy(() => import('./components/chat'));
const InternalChat = lazy(() => import('./components/internalChat'));
const newInternalChat = lazy(() => import('./components/internalChat/index'));

const Settings = lazy(() => import('./components/settings'));
const FAQ = lazy(() => import('./components/faq'));
const Reports = lazy(() => import('./components/reports'));
const CallLogs = lazy(() => import('./components/calllogs'));

// Lazy load contact management pages
const ContactsPage = lazy(() => import('./components/contactManage/pages/ContactsPage'));
const AgentsPage = lazy(() => import('./components/contactManage/pages/AgentsPage'));
const GroupsPage = lazy(() => import('./components/contactManage/pages/GroupsPage'));

// Lazy load legal pages

const NotFound = lazy(() => import('./components/common/NotFound'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Wrapper component for lazy-loaded routes
const LazyRoute = ({ children }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
);

// Root Redirect Component - handles root path based on auth status
const RootRedirect = () => {
  const { user, loading } = useAuth();
  
  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Otherwise, redirect to login
  return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

// Protected Route Component
export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

// Public Route Component (preserves current route if already logged in)
export const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If user is logged in and trying to access login/signup pages, redirect to dashboard
  // Otherwise, preserve the current route
  if (user && (location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/forgot-password' || location.pathname === '/reset-password')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Route configurations with lazy loading
export const routes = [
  {
    path: "/login",
    element: (
      <PublicRoute>
        <LazyRoute>
          <Login />
        </LazyRoute>
      </PublicRoute>
    )
  },
  {
    path: "/signup",
    element: (
      <PublicRoute>
        <LazyRoute>
          <Signup />
        </LazyRoute>
      </PublicRoute>
    )
  },
  {
    path: "/signup-success",
    element: (
      <PublicRoute>
        <LazyRoute>
          <SignupSuccess />
        </LazyRoute>
      </PublicRoute>
    )
  },
  {
    path: "/forgot-password",
    element: (
      <PublicRoute>
        <LazyRoute>
          <ForgotPassword />
        </LazyRoute>
      </PublicRoute>
    )
  },
  {
    path: "/reset-password",
    element: (
      <PublicRoute>
        <LazyRoute>
          <ResetPassword />
        </LazyRoute>
      </PublicRoute>
    )
  },
  
  {
    path: "/approve-user",
    element: (
      <LazyRoute>
        <ApproveUser />
      </LazyRoute>
    )
  },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <Dashboard />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/chat",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <Chat />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/internal-chat",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <InternalChat />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/manage-users",
    element: <Navigate to="/manage-users/contacts" replace />
  },
  {
    path: "/manage-users/contacts",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <ContactsPage />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/manage-users/agents",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <AgentsPage />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/manage-users/groups",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <GroupsPage />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/settings",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <Settings />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/faq",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <FAQ />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/reports",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <Reports />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/call-logs",
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <CallLogs />
        </LazyRoute>
      </ProtectedRoute>
    )
  },
  {
    path: "/",
    element: <RootRedirect />
  },
  {
    path: "*",
    element: (
      <LazyRoute>
        <NotFound />
      </LazyRoute>
    )
  }
];